/**
 * Pure projection of the WFS + WMS services layer lists into the GIS Services catalog, merged by
 * root layer id; the matching operational layer supplies preview tiles, bounds and metadata.
 * Kept free of stores so unit tests needn't load the GeoServer client.
 */

import type { TLayer, TServicesWfsLayer, TServicesWmsLayer } from '@safesystem/geoserver-api';
import {
	EGisProduct,
	type EGisModule,
	type IGisLayer,
	type IGisServicesSystemData,
	type TGisProductRecord
} from './interfaces';

const STUB_BBOX = { minx: 0, miny: 0, maxx: 0, maxy: 0 };

type TServiceEntry = { wfs?: TServicesWfsLayer; wms?: TServicesWmsLayer };

// `…_v2_3-wfs` and `…_v2_3-wms` share root `…_v2_3`.
const rootId = (id: string) => id.replace(/-(wfs|wms)$/, '');

// The library types geometryType as `string | null`; narrow to what the catalog renders.
function geometryTypeOf(raw: string | null | undefined): IGisLayer['geometryType'] {
	return raw === 'point' || raw === 'line' || raw === 'polygon' ? raw : null;
}

const serviceLink = (template = '') => ({
	layer: { template },
	capabilities: template && `${template.split('?')[0]}?request=GetCapabilities`
});

// Rich fields come from the matching operational layer, stubbed when there is none.
const previewFields = (p: TLayer | undefined) =>
	p
		? {
				abstract: p.abstract,
				attribution: p.attribution,
				nativeCrs: p.crs,
				latLonBbox: { ...p.latLonBbox },
				nativeBbox: { ...p.nativeBbox },
				featureCount: p.featureCount,
				geometryType: geometryTypeOf(p.geometryType),
				xyzTemplate: p.xyzTemplate
			}
		: {
				abstract: '',
				attribution: '',
				nativeCrs: '',
				latLonBbox: { ...STUB_BBOX },
				nativeBbox: { ...STUB_BBOX },
				featureCount: null,
				geometryType: null,
				xyzTemplate: ''
			};

function toGisLayer(
	id: string,
	{ wfs, wms }: TServiceEntry,
	preview: TLayer | undefined,
	product: EGisProduct,
	moduleId: EGisModule
): IGisLayer {
	const src = (wfs ?? wms)!;
	return {
		id,
		title: src.title,
		keywords: src.keywords,
		...previewFields(preview),
		defaultStyle: wms?.defaultStyle ?? preview?.style ?? '',
		additionalStyles: wms?.additionalStyles ?? [],
		hasWfs: !!wfs,
		hasWms: !!wms,
		services: { wfs: serviceLink(wfs?.wfsTemplate), wms: serviceLink(wms?.wmsTemplate) },
		product,
		module: moduleId
	};
}

// Display order; products without data are dropped during assembly.
const PRODUCT_ORDER: EGisProduct[] = Object.values(EGisProduct);

export function buildCatalog(
	wfsLayers: TServicesWfsLayer[],
	wmsLayers: TServicesWmsLayer[],
	operationalLayers: TLayer[]
): IGisServicesSystemData {
	const operationalById = new Map(operationalLayers.map((l) => [l.layer, l]));

	// One entry per root id, in first-seen (WFS then WMS) order.
	const entries = new Map<string, TServiceEntry>();
	for (const wfs of wfsLayers) entries.set(rootId(wfs.id), { ...entries.get(rootId(wfs.id)), wfs });
	for (const wms of wmsLayers) entries.set(rootId(wms.id), { ...entries.get(rootId(wms.id)), wms });

	const products: TGisProductRecord = {};
	const layerById: Record<string, IGisLayer> = {};

	for (const [id, entry] of entries) {
		const src = entry.wfs ?? entry.wms!;
		const productRaw = src.keywords.product;
		const moduleRaw = src.keywords.module;
		if (!productRaw || !moduleRaw) continue;

		// Skip products that aren't surfaced in this app yet.
		if (!PRODUCT_ORDER.includes(productRaw as EGisProduct)) continue;
		const product = productRaw as EGisProduct;
		const moduleId = moduleRaw as EGisModule;
		const preview = operationalById.get(id);
		// A layer with no operational match falls back to a version bucket from its keywords.
		const version =
			preview?.series ??
			src.keywords.series ??
			src.keywords.version ??
			src.keywords.dataset ??
			'current';

		const gisLayer = toGisLayer(id, entry, preview, product, moduleId);
		layerById[id] = gisLayer;

		(((products[product] ??= {})[version] ??= {})[moduleId] ??= []).push(gisLayer);
	}

	const productOrder = PRODUCT_ORDER.filter((p) => products[p]);
	return { products, layerById, productOrder };
}
